'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useTenant } from '@/components/storefront/TenantProvider';
import { buildNestApiUrl } from '@/lib/api-url';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  interest: z.string().optional(),
  message: z.string().min(1, 'Message is required'),
});

type FormValues = z.infer<typeof schema>;

interface ContactFormProps {
  tenantSlug?: string;
  vehicleId?: string;
  vehicleLabel?: string;
  primaryColor?: string;
}

export function ContactForm({ tenantSlug, vehicleId, vehicleLabel, primaryColor }: ContactFormProps) {
  const tenant = useTenant();
  const slug = tenantSlug ?? tenant.slug;
  const primary = primaryColor ?? tenant.primaryColor ?? '#3B82F6';

  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      interest: vehicleLabel ?? '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const res = await fetch(buildNestApiUrl(`/storefront/${slug}/contact`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
          vehicleId: vehicleId || undefined,
          message: values.interest
            ? `Interested in: ${values.interest}\n\n${values.message}`
            : values.message,
        }),
      });

      if (!res.ok) {
        const json = await res.json() as { error?: string };
        throw new Error(json.error ?? 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
        <div className="mb-3 text-4xl">✓</div>
        <h3 className="text-lg font-semibold text-green-800">Message Sent!</h3>
        <p className="mt-1 text-sm text-green-700">
          We&apos;ll be in touch with you shortly.
        </p>
      </div>
    );
  }

  const inputCls =
    'w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-shadow';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            {...register('name')}
            placeholder="Your full name"
            className={inputCls}
            style={{ '--tw-ring-color': primary } as React.CSSProperties}
          />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            {...register('email')}
            type="email"
            placeholder="you@example.com"
            className={inputCls}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Phone</label>
        <input
          {...register('phone')}
          type="tel"
          placeholder="(555) 000-0000"
          className={inputCls}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          I&apos;m interested in
        </label>
        <input
          {...register('interest')}
          placeholder="Vehicle year, make, model or general inquiry"
          className={inputCls}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register('message')}
          rows={4}
          placeholder="How can we help you?"
          className={`${inputCls} resize-none`}
        />
        {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-xl py-3.5 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{ backgroundColor: primary }}
      >
        {isSubmitting ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  );
}
